// formacodeUtils.js

const normalizeString = (str) => {
  if (!str) return '';
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const parseFormacodeData = (csvData) => {
  const lines = csvData.split('\n').filter(line => line.trim());
  if (lines.length < 2) return { formacodes: [], hierarchy: [] };

  const headers = lines[0].split('|').map(h => h.trim());
  const formacodes = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('|');
    if (values.length < headers.length) continue;
    
    const row = headers.reduce((obj, header, index) => {
      obj[header] = values[index]?.trim() || '';
      return obj;
    }, {});

    // Vérifier si les champs obligatoires pour le parsing sont présents
    if (!row.code || !row.descriptorRich || !row.hierarchyCode) {
        // console.warn("Ligne CSV ignorée car il manque code, descriptorRich ou hierarchyCode:", row);
        // continue; // Optionnel: ignorer la ligne si des champs clés manquent
    }

    const formacode = {
      code: row.code,
      descriptorRich: row.descriptorRich,
      normalizedDescriptor: row.normalizedDescriptor,
      normalizedSynonyms: row.normalizedSynonyms ? row.normalizedSynonyms.split('###').filter(Boolean) : [],
      normalizedCareerSynonyms: row.normalizedCareerSynonyms ? row.normalizedCareerSynonyms.split('###').filter(Boolean) : [],
      hierarchyCode: row.hierarchyCode,
    };
    
    formacodes.push(formacode);
  }
  
  const hierarchy = buildHierarchy(formacodes);
  
  return { formacodes, hierarchy };
};

// Construction de l'arbre hiérarchique basée sur hierarchyCode
const buildHierarchy = (formacodes) => {
  const nodeMapByCode = new Map(); // Mappe les objets noeuds par leur formacode.code
  const nodeMapByHierarchyCode = new Map(); // Mappe les objets noeuds par leur formacode.hierarchyCode
  const rootNodes = [];

  // Première passe : créer tous les nœuds et les mapper
  formacodes.forEach(formacode => {
    // S'assurer qu'un formacode avec un code vide ou sans hierarchyCode n'est pas traité
    // ou est traité spécifiquement si c'est un cas d'usage.
    // Pour la hiérarchie, un hierarchyCode est essentiel.
    if (!formacode.code) return; // Ignorer si pas de code interne

    const node = {
      ...formacode,
      children: []
    };
    nodeMapByCode.set(formacode.code, node);
    if (formacode.hierarchyCode) { // Un formacode ne peut être dans la hiérarchie que s'il a un hierarchyCode
        nodeMapByHierarchyCode.set(formacode.hierarchyCode, node);
    }
  });

  // Deuxième passe : lier les enfants aux parents
  nodeMapByCode.forEach(node => {
    // Un nœud ne peut être un enfant que s'il a un hierarchyCode et que ce code est plus long que celui d'une racine
    // (longueur 2 pour les racines comme "A0", "B1", etc.)
    if (node.hierarchyCode && node.hierarchyCode.length > 2) {
      // Le code hiérarchique parent est le code actuel moins les 2 derniers caractères
      // (ex: A0A0B1 -> parent A0A0)
      const parentHierarchyCode = node.hierarchyCode.slice(0, -2);
      const parentNode = nodeMapByHierarchyCode.get(parentHierarchyCode);
      
      if (parentNode) {
        parentNode.children.push(node);
      } else {
        // Si le parent n'est pas trouvé (données potentiellement manquantes ou code racine mal formé pour ce noeud)
        // On le considère comme une racine pour ne pas le perdre.
        // console.warn(`Parent avec hierarchyCode '${parentHierarchyCode}' non trouvé pour le nœud '${node.code}' (hierarchyCode: '${node.hierarchyCode}'). Ajouté comme racine.`);
        rootNodes.push(node);
      }
    } else if (node.hierarchyCode && node.hierarchyCode.length === 2) {
      // C'est un nœud racine de premier niveau (ex: A0, B1)
      rootNodes.push(node);
    } else if (!node.hierarchyCode) {
      // Cas des formacodes sans hierarchyCode : ils sont considérés comme des éléments isolés,
      // donc on peut les ajouter comme racines s'ils ne sont pas déjà enfants d'un autre noeud (peu probable ici).
      // console.log(`Formacode '${node.code}' sans hierarchyCode. Ajouté comme racine.`);
      // rootNodes.push(node); // Optionnel: si vous voulez les voir.
    }
    // Les noeuds avec hierarchyCode de longueur < 2 ou de longueur impaire (si non géré avant)
    // ne seront pas proprement hiérarchisés par cette logique.
  });
  
  // Filtrer les doublons potentiels dans rootNodes si différentes logiques peuvent y ajouter
  const uniqueRootNodes = [...new Set(rootNodes)];

  // Optionnel: trier les enfants et les racines
  const sortNodes = (nodes) => {
    nodes.sort((a, b) => {
        // Priorité au code hiérarchique pour l'ordre structurel
        if (a.hierarchyCode && b.hierarchyCode) {
            const compareHierarchy = a.hierarchyCode.localeCompare(b.hierarchyCode);
            if (compareHierarchy !== 0) return compareHierarchy;
        } else if (a.hierarchyCode) { // a a un code, b n'en a pas
            return -1;
        } else if (b.hierarchyCode) { // b a un code, a n'en a pas
            return 1;
        }
        // Ensuite, par descripteur
        return a.descriptorRich.localeCompare(b.descriptorRich);
    });
    nodes.forEach(node => {
      if (node.children.length > 0) {
        sortNodes(node.children);
      }
    });
  };
  sortNodes(uniqueRootNodes);

  return uniqueRootNodes;
};

export const searchFormacodes = (formacodes, searchText) => {
  if (!searchText.trim()) return [];
  const searchTerms = searchText.trim().split(/\s+/).map(normalizeString);
  const initialResults = [];
  
  formacodes.forEach(formacode => {
    // Only consider items that are not top-level categories for direct search matching
    // (e.g., "A0", "B1" which are length 2). This was implicit in your original code.
    // If you want to include top-level, adjust this condition.
    if(formacode.hierarchyCode && formacode.hierarchyCode.length > 2) {
      const scores = [];
      // Ensure normalized fields exist. If they come from CSV, they should be fine.
      // If they are supposed to be generated from descriptorRich/synonyms, that step is missing in this snippet.
      // For now, let's assume normalizedDescriptor, etc. are populated.
      // If not, they need to be normalized here or during parseFormacodeData.
      // Example: const normDesc = normalizeString(formacode.descriptorRich);
      
      const descriptorScore = calculateMatchScore(formacode.normalizedDescriptor || normalizeString(formacode.descriptorRich), searchTerms, 'descriptor');
      if (descriptorScore > 0) scores.push({ score: descriptorScore, type: 'descriptor' });
      
      (formacode.normalizedSynonyms || []).forEach(synonym => {
        const synonymScore = calculateMatchScore(synonym, searchTerms, 'synonym');
        if (synonymScore > 0) scores.push({ score: synonymScore, type: 'synonym' });
      });
      
      (formacode.normalizedCareerSynonyms || []).forEach(careerSynonym => {
        const careerScore = calculateMatchScore(careerSynonym, searchTerms, 'career');
        if (careerScore > 0) scores.push({ score: careerScore, type: 'career' });
      });
      
      if (scores.length > 0) {
        const bestScore = Math.max(...scores.map(s => s.score));
        const bestType = scores.find(s => s.score === bestScore).type;
        initialResults.push({ formacode, score: bestScore, matchType: bestType });
      }
    }
  });
  
  // Sort by score (desc) then by matchType preference
  initialResults.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    const typeOrder = { descriptor: 0, synonym: 1, career: 2 };
    return typeOrder[a.matchType] - typeOrder[b.matchType];
  });

  // ---- NEW HIERARCHICAL FILTERING LOGIC ----
  if (initialResults.length === 0) return [];

  const finalFilteredResults = [];
  let i = 0;
  while (i < initialResults.length) {
    const currentScore = initialResults[i].score;
    const groupWithSameScore = [];
    let j = i;
    // Collect all items with the current score
    while (j < initialResults.length && initialResults[j].score === currentScore) {
      groupWithSameScore.push(initialResults[j]);
      j++;
    }

    // Filter this group to keep only highest-level parents
    if (groupWithSameScore.length > 0) {
      // Sort by hierarchyCode length (shorter codes are parents, process them first)
      // This makes the filtering logic simpler.
      groupWithSameScore.sort((a, b) => 
        a.formacode.hierarchyCode.length - b.formacode.hierarchyCode.length
      );
      
      const subFilteredGroup = [];
      for (const currentItem of groupWithSameScore) {
        let isDescendant = false;
        for (const keptItem of subFilteredGroup) {
          // Check if currentItem.hierarchyCode starts with keptItem.hierarchyCode AND is longer
          if (currentItem.formacode.hierarchyCode.startsWith(keptItem.formacode.hierarchyCode) &&
              currentItem.formacode.hierarchyCode.length > keptItem.formacode.hierarchyCode.length) {
            isDescendant = true;
            break;
          }
        }
        if (!isDescendant) {
          subFilteredGroup.push(currentItem);
        }
      }
      finalFilteredResults.push(...subFilteredGroup);
    }
    i = j; // Move to the start of the next score group
  }
  
  return finalFilteredResults;
};

const calculateMatchScore = (text, searchTerms, type) => {
  if (!text) return 0; // Handle cases where text might be undefined/null
  let score = 0;
  let foundTerms = 0;
  let orderBonus = 0;
  
  const normalizedText = normalizeString(text); // Ensure text is normalized if not already

  searchTerms.forEach((term, index) => {
    // term is already normalized
    const termIndex = normalizedText.indexOf(term);
    if (termIndex !== -1) {
      foundTerms++;
      if (normalizedText === term) score += 100; // Correspondance exacte
      else if (new RegExp(`\\b${term}\\b`).test(normalizedText)) score += 50; // mot entier
      else if (termIndex === 0) score += 20; // Début de chaîne
      else score += 10; // Correspondance partielle
      
      if (index === 0 && termIndex === 0) orderBonus += 5;
    }
  });
  
  if (foundTerms === searchTerms.length && searchTerms.length > 0) score += 20;
  
  const typeBonus = { descriptor: 10, synonym: 7, career: 7 };
  return foundTerms > 0 ? score + orderBonus + (typeBonus[type] || 0) : 0;
};

export { normalizeString };